import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supplierAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  ShoppingCartIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon,
  TruckIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  { value: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-700' },
  { value: 'preparing', label: 'Preparing', color: 'bg-purple-100 text-purple-700' },
  { value: 'ready', label: 'Ready', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'shipped', label: 'Shipped', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' }
];

const ITEMS_PER_PAGE = 10;

const SupplierOrders = () => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState({
    status: '',
    paymentStatus: '',
    search: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'placedAt',
    sortOrder: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const res = await supplierAPI.listOrders({});
      setOrders(res.data.orders || []);
    } catch (error) {
      console.error('Load orders error:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort orders
  const filteredOrders = useMemo(() => {
    let result = [...orders];

    // Filter by status
    if (filter.status) {
      result = result.filter(o => o.status === filter.status);
    }

    // Filter by payment status
    if (filter.paymentStatus) {
      result = result.filter(o => o.paymentStatus === filter.paymentStatus);
    }

    // Filter by search (order number, customer name, phone)
    if (filter.search) {
      const search = filter.search.toLowerCase();
      result = result.filter(o =>
        o.orderNumber?.toLowerCase().includes(search) ||
        o.contactName?.toLowerCase().includes(search) ||
        o.contactPhone?.includes(search)
      );
    }

    // Filter by date range
    if (filter.dateFrom) {
      const fromDate = new Date(filter.dateFrom);
      result = result.filter(o => new Date(o.placedAt) >= fromDate);
    }
    if (filter.dateTo) {
      const toDate = new Date(filter.dateTo);
      toDate.setHours(23, 59, 59);
      result = result.filter(o => new Date(o.placedAt) <= toDate);
    }

    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      switch (filter.sortBy) {
        case 'totalAmount':
          aVal = Number(a.totalAmount || 0);
          bVal = Number(b.totalAmount || 0);
          break;
        case 'contactName':
          aVal = a.contactName?.toLowerCase() || '';
          bVal = b.contactName?.toLowerCase() || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        default:
          aVal = new Date(a.placedAt);
          bVal = new Date(b.placedAt);
      }
      if (filter.sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return result;
  }, [orders, filter]);

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredOrders.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredOrders, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const updateStatus = async (orderId, newStatus, order = null) => {
    // Find the order to check payment status
    const targetOrder = order || orders.find(o => o.id === orderId) || selectedOrder;
    
    // Prevent shipping unpaid orders
    if (newStatus === 'shipped' && targetOrder?.paymentStatus !== 'paid') {
      toast.error('Cannot ship order - waiting for farmer payment. Please wait for payment to be confirmed before shipping.');
      return;
    }
    
    setUpdatingStatus(true);
    try {
      await supplierAPI.updateOrderStatus(orderId, newStatus);
      toast.success(`Order ${newStatus}`);
      // Optimistic update for instant UI feedback
      setOrders(prevOrders => 
        prevOrders.map(o => o.id === orderId ? { ...o, status: newStatus } : o)
      );
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => prev ? { ...prev, status: newStatus } : prev);
      }
      // Also refresh from server for consistency
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update order status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return <span className={`inline-flex px-2 py-1 text-xs rounded-full ${opt.color}`}>{opt.label}</span>;
  };

  const clearFilters = () => {
    setFilter({
      status: '',
      paymentStatus: '',
      search: '',
      dateFrom: '',
      dateTo: '',
      sortBy: 'placedAt',
      sortOrder: 'desc'
    });
  };

  const handleSort = (column) => {
    setFilter(prev => ({
      ...prev,
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIndicator = ({ column }) => {
    if (filter.sortBy !== column) return null;
    return <span className="ml-1">{filter.sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-2xl p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 backdrop-blur rounded-xl">
              <ShoppingCartIcon className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Orders</h1>
              <p className="text-blue-100">Manage orders from farmers</p>
            </div>
          </div>
        </div>
        
        {/* Stats */}
        <div className="relative mt-6 grid grid-cols-4 gap-3">
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{orders.length}</p>
            <p className="text-blue-200 text-xs">Total</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{orders.filter(o => o.status === 'pending').length}</p>
            <p className="text-blue-200 text-xs">Pending</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{orders.filter(o => ['confirmed', 'preparing', 'ready', 'shipped'].includes(o.status)).length}</p>
            <p className="text-blue-200 text-xs">Processing</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{orders.filter(o => o.status === 'delivered').length}</p>
            <p className="text-blue-200 text-xs">Delivered</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-700">Filters</span>
          {(filter.status || filter.paymentStatus || filter.search || filter.dateFrom || filter.dateTo) && (
            <button
              onClick={clearFilters}
              className="ml-2 text-sm text-blue-600 hover:text-blue-700"
            >
              Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search order, customer..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            />
          </div>

          {/* Status Filter */}
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          >
            <option value="">All Status</option>
            {STATUS_OPTIONS.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          {/* Payment Filter */}
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filter.paymentStatus}
            onChange={(e) => setFilter({ ...filter, paymentStatus: e.target.value })}
          >
            <option value="">All Payment</option>
            <option value="pending">Unpaid</option>
            <option value="paid">Paid</option>
          </select>

          {/* Date From */}
          <input
            type="date"
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filter.dateFrom}
            onChange={(e) => setFilter({ ...filter, dateFrom: e.target.value })}
          />

          {/* Date To */}
          <input
            type="date"
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filter.dateTo}
            onChange={(e) => setFilter({ ...filter, dateTo: e.target.value })}
          />
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          Showing {paginatedOrders.length} of {filteredOrders.length} orders
          {filteredOrders.length !== orders.length && ` (${orders.length} total)`}
        </span>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-100">
          <ShoppingCartIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No orders yet</h3>
          <p className="text-gray-500">Orders from farmers will appear here</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Mobile Card View */}
          <div className="block md:hidden divide-y divide-gray-200">
            {paginatedOrders.map((order) => (
              <div key={order.id} className="p-4 hover:bg-gray-50" onClick={() => setSelectedOrder(order)}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold text-gray-900">#{order.orderNumber}</p>
                    <p className="text-xs text-gray-500">{new Date(order.placedAt).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">KES {Number(order.totalAmount || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{order.items?.length || 0} items</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-700">{order.contactName}</p>
                    <p className="text-xs text-gray-500">{order.contactPhone}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(order.status)}
                    <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                      order.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {order.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('placedAt')}
                  >
                    Order <SortIndicator column="placedAt" />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('contactName')}
                  >
                    Customer <SortIndicator column="contactName" />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('totalAmount')}
                  >
                    Total <SortIndicator column="totalAmount" />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    Status <SortIndicator column="status" />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="font-medium text-gray-900">#{order.orderNumber}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.placedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm text-gray-900">{order.contactName}</p>
                        <p className="text-xs text-gray-500">{order.contactPhone}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm text-gray-900">{order.items?.length || 0} items</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="font-medium text-gray-900">
                        KES {Number(order.totalAmount || 0).toLocaleString()}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        order.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {order.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 md:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                {/* Page numbers - show fewer on mobile */}
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-2 text-sm rounded-lg ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                {/* Mobile: just show current/total */}
                <span className="sm:hidden px-3 py-2 text-sm bg-blue-600 text-white rounded-lg">
                  {currentPage}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Order #{selectedOrder.orderNumber}</h3>
                  <p className="text-sm text-gray-500">
                    Placed on {new Date(selectedOrder.placedAt).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Order Status</p>
                  <div className="mt-1">{getStatusBadge(selectedOrder.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment</p>
                  <span className={`inline-flex mt-1 px-2 py-1 text-xs rounded-full ${
                    selectedOrder.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {selectedOrder.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                  </span>
                </div>
              </div>

              {/* Customer Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Customer Details</h4>
                <div className="space-y-2">
                  <p className="text-sm">
                    <span className="text-gray-500">Name:</span> {selectedOrder.contactName}
                  </p>
                  <p className="text-sm flex items-center">
                    <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                    {selectedOrder.contactPhone}
                  </p>
                  {selectedOrder.deliveryAddress && (
                    <p className="text-sm flex items-start">
                      <MapPinIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                      {selectedOrder.deliveryAddress}
                    </p>
                  )}
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Order Items</h4>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, i) => (
                    <div key={i} className="flex justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{item.productName}</p>
                        <p className="text-sm text-gray-500">
                          {item.quantity} x KES {Number(item.unitPrice).toLocaleString()}
                        </p>
                      </div>
                      <p className="font-medium text-gray-900">
                        KES {Number(item.total).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Subtotal</span>
                    <span>KES {Number(selectedOrder.subtotal || 0).toLocaleString()}</span>
                  </div>
                  {selectedOrder.deliveryFee > 0 && (
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-500">Delivery</span>
                      <span>KES {Number(selectedOrder.deliveryFee).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold mt-2 pt-2 border-t border-gray-200">
                    <span>Total</span>
                    <span>KES {Number(selectedOrder.totalAmount || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm text-green-600 mt-1">
                    <span>Your Earnings</span>
                    <span>KES {Number(selectedOrder.supplierAmount || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Update Status */}
              {selectedOrder.status !== 'delivered' && selectedOrder.status !== 'cancelled' && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Update Status</h4>
                  
                  {/* Warning for unpaid orders */}
                  {selectedOrder.paymentStatus !== 'paid' && (
                    <div className="mb-3 p-2 bg-yellow-100 border border-yellow-300 rounded-lg text-sm text-yellow-800">
                      ⚠️ Waiting for farmer payment. You cannot ship until payment is confirmed.
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.filter(s => s.value !== selectedOrder.status && s.value !== 'cancelled').map(s => {
                      // Disable shipped button if not paid
                      const isShipDisabled = s.value === 'shipped' && selectedOrder.paymentStatus !== 'paid';
                      
                      return (
                        <button
                          key={s.value}
                          onClick={() => !isShipDisabled && updateStatus(selectedOrder.id, s.value)}
                          disabled={updatingStatus || isShipDisabled}
                          className={`px-3 py-2 text-sm bg-white border rounded-lg disabled:opacity-50 ${
                            isShipDisabled 
                              ? 'border-yellow-300 text-yellow-600 cursor-not-allowed' 
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                          title={isShipDisabled ? 'Cannot ship until farmer pays' : `Mark as ${s.label}`}
                        >
                          {s.value === 'confirmed' && <CheckCircleIcon className="h-4 w-4 inline mr-1" />}
                          {s.value === 'shipped' && <TruckIcon className="h-4 w-4 inline mr-1" />}
                          {s.value === 'delivered' && <CheckCircleIcon className="h-4 w-4 inline mr-1" />}
                          {isShipDisabled ? 'Awaiting Payment' : `Mark as ${s.label}`}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => updateStatus(selectedOrder.id, 'cancelled')}
                      disabled={updatingStatus}
                      className="px-3 py-2 text-sm bg-white border border-red-300 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      <XCircleIcon className="h-4 w-4 inline mr-1" />
                      Cancel Order
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierOrders;
